import { GradeRange } from "../GradeRange/GradeRange";
import React from "react";
import { Tooltip } from "primereact/tooltip";
import { useTranslation } from "react-i18next";
import "./BodyTemplateStyles.css";

const INSPECTION_STATUS_FULL_REPORT = "FULL_REPORT";
const INSPECTION_STATUS_FULL_REPORT_NUMBER = "7";

export type AiGradeInfo = {
    aiGradeFilterField?: string | null;
    predictedConfidence?: number;
    predictedGrade?: number;
    predictedGradeMin?: number;
    predictedGradeMax?: number;
    evolutionConfidence?: number;
    evolutionGrade?: number;
    evolutionGradeMin?: number;
    evolutionGradeMax?: number;
};

export type VesselGrade = {
    grade: number | undefined;
    gradeMin: number | undefined;
    gradeMax: number | undefined;
    gradeType: GradeType;
    gradeFilterField?: string | null;
};

export interface GradeData {
    idwalGrade: string;
    status: string;
    guid: string;
    imo: string;
    aiGrades?: never | undefined;
}

export const idwalGradeBodyTemplate = (
    gradeData: GradeData,
    aiGradingActive: boolean,
    href: string | undefined,
    openInWindow = false
) => {
    const { t } = useTranslation("common");

    const grades = getVesselGrade(
        aiGradingActive,
        gradeData.idwalGrade,
        gradeData.status,
        gradeData.aiGrades
    );

    if (!grades) {
        return <React.Fragment />;
    }

    const gradeComponent = getGradeComponent(grades);
    if (!gradeComponent) {
        return <React.Fragment />;
    }

    const reportAvailable =
        gradeData.guid &&
        (gradeData.status === INSPECTION_STATUS_FULL_REPORT ||
            gradeData.status === INSPECTION_STATUS_FULL_REPORT_NUMBER);
    const targetClass = `tooltip-${
        gradeData.imo.toLowerCase() === "n/a" ? gradeData.guid : gradeData.imo
    }`; // Attaches tooltip to div component by sharing the target class between them

    const toolTipHref = () => {
        if (openInWindow) {
            return (
                <a href={href} rel="noopener" className="view-report-link">
                    {`${t("tooltips.viewReport")}`}
                </a>
            );
        }

        return (
            <a
                href={href}
                target={"_blank"}
                rel="noopener"
                className="view-report-link"
            >
                {`${t("tooltips.viewReport")}`}
            </a>
        );
    };

    const tooltipComponent = (
        <Tooltip
            autoHide={false}
            target={`.${targetClass}`}
            data-cy={targetClass}
        >
            {toolTipHref()}
        </Tooltip>
    );

    const reportAvailableLink = () => {
        if (openInWindow) {
            return (
                <a
                    href={href}
                    rel="noopener"
                    className={`view-report-link ${
                        reportAvailable ? targetClass : ""
                    }`}
                    data-pr-position="right"
                    data-pr-my="left center-2"
                >
                    {gradeComponent}
                </a>
            );
        }
        return (
            <a
                href={href}
                target={"_blank"}
                rel="noopener"
                className={`view-report-link ${
                    reportAvailable ? targetClass : ""
                }`}
                data-pr-position="right"
                data-pr-my="left center-2"
            >
                {gradeComponent}
            </a>
        );
    };

    return (
        <React.Fragment>
            <div className={`idwal-grade flex justify-content-center`}>
                {reportAvailable ? reportAvailableLink() : gradeComponent}
            </div>
            {reportAvailable ? tooltipComponent : <></>}
        </React.Fragment>
    );
};

export const getGradeComponent = (grades: any) => {
    let gradeComponent = null;
    switch (grades.gradeType) {
        case GradeType.IDWAL:
            gradeComponent = <GradeRange grade={grades.grade} />;
            break;
        case GradeType.PREDICTED:
            if (!grades.gradeMin || !grades.gradeMax) {
                if (!grades.grade) {
                    return null;
                }

                gradeComponent = <GradeRange grade={grades.grade} />;
            } else {
                gradeComponent = (
                    <GradeRange
                        gradeMin={grades.gradeMin}
                        gradeMax={grades.gradeMax}
                    />
                );
            }
            break;
        case GradeType.EVOLUTION:
            if (grades.gradeMin && grades.gradeMax) {
                gradeComponent = (
                    <GradeRange
                        gradeMin={grades.gradeMin}
                        gradeMax={grades.gradeMax}
                    />
                );
            } else {
                if (!grades.grade) {
                    return null;
                }

                gradeComponent = <GradeRange grade={grades.grade} />;
            }
            break;
    }

    return gradeComponent;
};

export function getVesselGrade(
    aiGradingActive: boolean,
    idwalGrade?: string,
    inspectionStatus?: string,
    aiGrades?: AiGradeInfo
) {
    const inspectionFullReportAndGrade =
        idwalGrade &&
        (inspectionStatus === INSPECTION_STATUS_FULL_REPORT ||
            inspectionStatus === INSPECTION_STATUS_FULL_REPORT_NUMBER);
    if (inspectionFullReportAndGrade) {
        if (aiGradingActive && aiGrades?.evolutionGrade) {
            return {
                grade: roundGrade(aiGrades.evolutionGrade),
                gradeMin: roundGrade(aiGrades.evolutionGradeMin),
                gradeMax: roundGrade(aiGrades.evolutionGradeMax),
                gradeType: GradeType.EVOLUTION,
                gradeFilterField: aiGrades.evolutionGradeMin
                    ? `${aiGrades.evolutionGradeMin}-${aiGrades.evolutionGradeMax}`
                    : aiGrades.evolutionGrade
            } as VesselGrade;
        }

        return {
            grade: idwalGrade ? parseInt(idwalGrade) : "",
            gradeMin: undefined,
            gradeMax: undefined,
            gradeType: GradeType.IDWAL,
            gradeFilterField: `${idwalGrade}`
        } as VesselGrade;
    }

    if (aiGradingActive && aiGrades) {
        return {
            grade: roundGrade(aiGrades.predictedGrade),
            gradeMin: roundGrade(aiGrades.predictedGradeMin),
            gradeMax: roundGrade(aiGrades.predictedGradeMax),
            gradeType: GradeType.PREDICTED,
            gradeFilterField: aiGrades.predictedGradeMin
                ? `${aiGrades.predictedGradeMin}-${aiGrades.predictedGradeMax}`
                : aiGrades.predictedGrade
        } as VesselGrade;
    }

    return null;
}

function roundGrade(grade?: number) {
    return grade ? Math.round(grade) : undefined;
}

export enum GradeType {
    IDWAL = 0,
    PREDICTED = 1,
    EVOLUTION = 2
}
